import { JSX } from 'react';

// Components
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtNavLink } from '@/components/atoms/at-nav-link/at-nav-link';

// Types
import { EThemes } from '@/lib/contentstack-sdk/types';
import { IHousePlanHit } from '@/lib/search/types';
import { IMlMapCardProps } from './ml-map-card.types';
import { IDesignStudio } from '@/components/organisms/or-design-studio-block/or-design-studio-block.types';
import { ENavLinkVariants } from '@/components/atoms/at-nav-link/at-nav-link.types';
import {
  ETextVariants,
  ETextSize,
} from '@/components/atoms/at-text/at-text.types';
import {
  EClickableSize,
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';

// Hooks + Utils
import {
  sendHomePlanClickGTMEvent,
  sendSelectLocationOnMapGTMEvent,
} from '@/components/helpers/analytics/gtm';
import { formatModelHomeUrl } from '@/components/helpers/formatters/model-homes';
import { AddressGenerator } from '@/components/helpers/formatters/AddressBuilder';
import classNames from 'classnames';
import Link from 'next/link';

/**
 * Renders up to 6 model home links associated with a given design studio.
 * Each card includes the plan name, exterior style, and a thumbnail image.
 * Clicking a card fires a GTM event and navigates to the model home detail page.
 */
const renderRelatedModels = (
  modelHomes: IHousePlanHit[],
  studioData: IDesignStudio,
) => {
  if (!modelHomes || modelHomes.length === 0) {
    return <></>;
  }

  const handleClick = (
    modelName: string,
    modelId: number,
    modelUrl: string,
  ) => {
    sendHomePlanClickGTMEvent(modelName, modelId, modelUrl);
  };

  return modelHomes
    .slice(0, 6)
    .map(({ ExteriorStyle, Plan, Image, Id, cms }, i) => {
      return (
        <Link
          className="relative flex"
          data-testid="model-card"
          key={`${i}-${ExteriorStyle.Name}`}
          href={formatModelHomeUrl(
            studioData?.CsName || studioData?.Name,
            Plan.Name,
            ExteriorStyle.Name,
          )}
          target={EClickableTarget.SELF}
          onClick={() =>
            handleClick(
              Plan.Name,
              Id as number,
              formatModelHomeUrl(
                studioData?.CsName || studioData?.Name,
                Plan.Name,
                ExteriorStyle.Name,
              ),
            )
          }
        >
          <div className="mr-6 h-120 w-170">
            <AtMedia
              src={{
                desktop:
                  cms?.card_image?.mobile_image_url?.url || (Image as string),
                tablet:
                  cms?.card_image?.mobile_image_url?.url || (Image as string),
                mobile:
                  cms?.card_image?.mobile_image_url?.url || (Image as string),
              }}
              priority={false}
              alt=""
              decorative
            />
          </div>
          <div className="flex w-full flex-col justify-center">
            <AtHeader
              element="p"
              variant="h4"
              className="mb-2 text-22 md:mb-3 md:text-32"
            >
              {Plan.Name}
            </AtHeader>

            <AtHeader
              element="p"
              variant="h6"
              className="text-13 text-skin-subhead-primary md:text-2xl"
            >
              {ExteriorStyle?.Name}
            </AtHeader>
          </div>
        </Link>
      );
    });
};

/**
 * MlMapCard is the main side-panel UI for the interactive mapping tool.
 * It displays detailed information about a selected design studio, including address, hours,
 * related model homes, and links to studio pages.
 *
 * This component is rendered when a user selects a location either by:
 * - Clicking a pin on the map
 * - Selecting a location from the search container
 *
 * It uses `studioData` to:
 * - Display address (unless the studio is virtual)
 * - Render business hours
 * - Show related model homes (up to 6)
 * - Provide links to the full studio detail page and Google Maps directions
 */
export const MlMapCard = ({
  studioData,
  onClose,
  theme = EThemes.DARK,
}: IMlMapCardProps): JSX.Element => {
  const handleClose = () => {
    onClose?.();
  };

  return (
    <aside
      data-theme={theme}
      data-testid="map-card"
      className="w-full max-w-598 rounded-40"
    >
      <header className="flex flex-row items-center justify-end px-6 pb-1 pt-6">
        <AtNavLink
          size={EClickableSize.SM}
          target={EClickableTarget.SELF}
          variant={ENavLinkVariants.CTA}
          className="min-w-auto z-10 !m-0 box-border w-auto bg-transparent !p-0 text-left text-3xl lg:ml-0"
          onClick={handleClose}
          ariaLabel="Close"
          shouldSendGTMEvent={false}
        >
          <span className="text-32 lg:text-58" data-theme={theme}>
            <AtIcon
              icon={EIcons.NAV_CLOSE}
              className="rounded-full bg-skin-ui-fill-primary"
            />
          </span>
        </AtNavLink>
      </header>
      <div className="px-6 pb-8 lg:px-12 lg:pb-15">
        <div className="flex flex-col gap-8">
          {(studioData.CsName || studioData.Name) && (
            <AtHeader element="h3" variant="h4" className="h4-sm">
              {studioData?.CsName || studioData?.Name} Design Studio
            </AtHeader>
          )}

          <div
            className={classNames([
              'grid grid-cols-1 gap-4',
              { 'md:grid-cols-2': !studioData?.IsVirtual },
            ])}
          >
            {!studioData?.IsVirtual && (
              <div className="flex flex-col gap-0 rounded-15 bg-skin-secondary p-5">
                <AtText variant={ETextVariants.LABEL} className="pb-3">
                  Location
                </AtText>

                <AtText type="span" size={ETextSize.MD}>
                  <AddressGenerator studioData={studioData} />
                </AtText>
                {studioData?.directionsLink && (
                  <AtClickable
                    className="mb-0 mt-7 w-fit pb-0 lg:mb-6"
                    variant={EClickableVariants.PRIMARY}
                    size={EClickableSize.SM}
                    type={EClickableTypes.LINK}
                    href={studioData?.directionsLink}
                    interactLocation={'map'}
                    target={EClickableTarget.BLANK}
                    label="Get Directions"
                  />
                )}
              </div>
            )}

            <div className="flex flex-col gap-0 rounded-15 bg-skin-secondary p-5">
              <AtText variant={ETextVariants.LABEL} className="pb-3">
                Hours
              </AtText>
              <ul className="lg:pb-10">
                {studioData?.hours &&
                  studioData?.hours.map((date) => {
                    const {
                      days,
                      hours,
                      _metadata: { uid },
                    } = date.dates;
                    return (
                      <li key={uid}>
                        <AtText size={ETextSize.MD}>
                          <span className="font-semibold">{days}: </span>
                          {hours}
                        </AtText>
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>
          <AtClickable
            className="w-fit"
            variant={EClickableVariants.PRIMARY}
            type={EClickableTypes.CTA}
            label="View Design Studio"
            target={EClickableTarget.SELF}
            outline
            href={studioData?.pageUrl}
            size={EClickableSize.LG}
            interactLocation="map"
            shouldSendGTMEvent={false}
            customGTMClick={() => {
              sendSelectLocationOnMapGTMEvent(
                'View Design Studio',
                studioData.pageUrl || studioData.Name,
              );
            }}
          />
          {studioData?.relatedModels &&
            studioData?.relatedModels?.length > 0 && (
              <div>
                <AtText variant={ETextVariants.LABEL} className="pb-6">
                  Available Model Homes
                </AtText>

                <div className="custom-scrollbar flex max-h-178 flex-col gap-5 overflow-y-auto pr-4 md:gap-8 md:pr-16">
                  {studioData?.relatedModels &&
                    studioData?.relatedModels?.length > 0 &&
                    renderRelatedModels(studioData.relatedModels, studioData)}
                </div>
              </div>
            )}
        </div>
      </div>
    </aside>
  );
};
